import { useMemo, useState } from "react";
import { useDispositionQuery } from "../../../../Services/ticket/CreateTicket";
import { useSaveDespositionMutation } from "../../../../Services/general/General";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../LoadingSpinner";
import { capitalizeFirstLetter } from "../../../reusableComponent/CapitalLetter";

type DispositionNode = {
  id: number;
  label?: string;
  topic?: string;
  priority?: string;
  is_sl_enabled?: boolean;
  ticket_name?: unknown;
  team?: unknown;
  hide?: unknown;
  tn_parent?: unknown;
  sub_topics?: DispositionNode[];
  level?: string;
  [key: string]: unknown;
};

function findNodeById(
  nodes: DispositionNode[],
  targetId: number,
): DispositionNode | null {
  for (const n of nodes || []) {
    if (n == null) continue;
    if (Number(n.id) === Number(targetId)) return n;
    if (n.sub_topics?.length) {
      const found = findNodeById(n.sub_topics, targetId);
      if (found) return found;
    }
  }
  return null;
}

function buildDispositionSlBody(
  node: DispositionNode,
  is_sl_enabled: boolean,
  teamFallback: string | number,
): Record<string, unknown> {
  const topic = String(node.topic ?? node.label ?? "").trim();
  return {
    id: node.id,
    topic,
    priority: node.priority ?? null,
    ticket_name: node.ticket_name ?? null,
    team: node.team ?? teamFallback,
    hide: node.hide ?? false,
    tn_parent: node.tn_parent ?? null,
    is_sl_enabled,
  };
}

function normalizeDispositionTree(raw: unknown): DispositionNode[] {
  if (Array.isArray(raw)) return raw as DispositionNode[];
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.results)) return o.results as DispositionNode[];
    if (Array.isArray(o.data)) return o.data as DispositionNode[];
  }
  return [];
}

/** Same level indexing as General Settings → Disposition (tree expand/collapse). */
function alignDispositionTree(arr: DispositionNode[]): DispositionNode[] {
  function addLevels(
    subTopics: DispositionNode[],
    levelPrefix: string,
  ): DispositionNode[] {
    return subTopics.map((item, index) => {
      const newItem = { ...item, level: `${levelPrefix}${index}` };
      if (newItem.sub_topics && newItem.sub_topics.length > 0) {
        newItem.sub_topics = addLevels(
          newItem.sub_topics,
          `${newItem.level}:`,
        );
      }
      return newItem;
    });
  }

  return arr.map((item, index) => {
    const newItem = { ...item, level: `${index}` };
    if (newItem.sub_topics && newItem.sub_topics.length > 0) {
      newItem.sub_topics = addLevels(newItem.sub_topics, `${index}:`);
    }
    return newItem;
  });
}

function findLeaves(node: DispositionNode): DispositionNode[] {
  if (!Array.isArray(node.sub_topics) || node.sub_topics.length === 0) {
    return [node];
  }
  return node.sub_topics.flatMap(findLeaves);
}

/**
 * Leaf used by a branch card's top toggle. Prefer a single terminal leaf under
 * the branch so the parent row reflects last-hierarchy SL without expanding.
 */
function findBranchLeaf(branchChild: DispositionNode): DispositionNode | null {
  const leaves = findLeaves(branchChild);
  return leaves.length === 1 ? leaves[0] : null;
}

type PriorityBasedInclusionListProps = {
  /** Team whose disposition tree is loaded (team is chosen on the parent page). */
  teamId: string;
};

/**
 * General Settings → SL: which leaf dispositions participate in disposition-based SL.
 * A parent with N children renders as N cards (one branch each). Cards start
 * collapsed; top toggle reflects the last hierarchy leaf (payload is leaf-only).
 */
const PriorityBasedInclusionList = ({ teamId }: PriorityBasedInclusionListProps) => {
  const [pendingDispositionId, setPendingDispositionId] = useState<
    number | null
  >(null);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  const {
    data: dispositionData,
    isFetching: dispositionLoading,
    refetch: refetchDispositions,
  } = useDispositionQuery(
    teamId === "" || teamId == null ? undefined : String(teamId),
    {
      skip: !teamId,
    },
  );

  const [saveDisposition] = useSaveDespositionMutation();
  const dispositionTree = useMemo(
    () => normalizeDispositionTree(dispositionData),
    [dispositionData],
  );

  const alignedTree = useMemo(
    () => alignDispositionTree(dispositionTree),
    [dispositionTree],
  );

  const toggleNode = (level: string) => {
    setExpandedNodes((prev) =>
      prev.includes(level)
        ? prev.filter((n) => n !== level)
        : [...prev, level],
    );
  };
  const handleDispositionSlToggle = async (row: {
    id: number;
    is_sl_enabled: boolean;
  }) => {
    if (!teamId || dispositionTree.length === 0) return;

    const node = findNodeById(dispositionTree, row.id);
    if (!node) {
      toast.error("Disposition not found.");
      return;
    }

    const nextEnabled = !row.is_sl_enabled;
    setPendingDispositionId(row.id);

    const body = buildDispositionSlBody(node, nextEnabled, teamId);

    try {
      await saveDisposition({
        id: teamId,
        body,
      }).unwrap();
      toast.success(
        `SL for disposition ${nextEnabled ? "enabled" : "disabled"}.`,
      );
      await refetchDispositions();
    } catch {
      toast.error("Failed to update disposition SL setting.");
    } finally {
      setPendingDispositionId(null);
    }
  };

  if (!teamId) return null;

  const nodeLabel = (node: DispositionNode) =>
    capitalizeFirstLetter(
      String(node.topic ?? node.label ?? "").trim() || `ID ${node.id}`,
    );

  /**
   * One card per child branch:
   * Incident → Bug | Incident → Incident2  (not both under one Incident).
   * Leaf roots stay a single card.
   */
  type BranchCard = {
    root: DispositionNode;
    branchChild: DispositionNode | null;
    cardKey: string;
    parentExpandKey: string;
    /** Last hierarchy leaf for this branch (top toggle target). */
    leaf: DispositionNode | null;
  };

  const branchCards: BranchCard[] = alignedTree.flatMap((root) => {
    const rootLevel = root.level ?? String(root.id);
    const children = Array.isArray(root.sub_topics) ? root.sub_topics : [];
    if (children.length === 0) {
      return [
        {
          root,
          branchChild: null,
          cardKey: rootLevel,
          parentExpandKey: rootLevel,
          leaf: root,
        },
      ];
    }
    return children.map((child, index) => {
      const childLevel = child.level ?? `${rootLevel}:${index}`;
      const cardKey = `${rootLevel}::${childLevel}`;
      return {
        root,
        branchChild: child,
        cardKey,
        parentExpandKey: `branch:${cardKey}`,
        leaf: findBranchLeaf(child),
      };
    });
  });

  const renderLeafToggle = (node: DispositionNode) => {
    const isSl = Boolean(node.is_sl_enabled);
    return (
      <div
        className="d-flex align-items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className={`badge ${
            isSl ? "badge-light-success" : "badge-light-danger"
          } fs-8`}
        >
          {isSl ? "Active" : "Inactive"}
        </span>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={isSl}
            disabled={pendingDispositionId === node.id}
            onChange={() =>
              handleDispositionSlToggle({
                id: node.id,
                is_sl_enabled: isSl,
              })
            }
            style={{
              cursor:
                pendingDispositionId === node.id ? "not-allowed" : "pointer",
            }}
          />
        </div>
      </div>
    );
  };

  /** Nested tree inside a branch card — click to open sub hierarchy. */
  const renderTreeNode = (
    node: DispositionNode,
    /** Leaf already shown on the card top row — do not duplicate its toggle. */
    topToggleLeafId: number | null = null,
  ) => {
    const level = node.level ?? String(node.id);
    const hasChildren =
      Array.isArray(node.sub_topics) && node.sub_topics.length > 0;
    const isLeaf = !hasChildren;
    const isExpanded = expandedNodes.includes(level);
    const showToggle =
      isLeaf &&
      (topToggleLeafId == null || Number(node.id) !== Number(topToggleLeafId));

    return (
      <div className="ms-8 mb-2">
        <div className="d-flex align-items-center justify-content-between">
          <div
            className="d-flex align-items-center"
            style={{ cursor: hasChildren ? "pointer" : "default" }}
            onClick={() => {
              if (hasChildren) toggleNode(level);
            }}
          >
            <i
              className={`bi ${
                hasChildren
                  ? `bi-chevron-${isExpanded ? "down" : "right"}`
                  : "bi-dash"
              } me-2`}
            />
            <span>{nodeLabel(node)}</span>
          </div>
          {showToggle && renderLeafToggle(node)}
        </div>
        {isExpanded && hasChildren && (
          <div className="ms-4 border-start ps-2 mt-2">
            {node.sub_topics!.map((subNode, index) => (
              <div key={subNode.level ?? `${level}-${index}`}>
                {renderTreeNode(subNode, topToggleLeafId)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderBranchCard = ({
    root,
    branchChild,
    cardKey,
    parentExpandKey,
    leaf,
  }: BranchCard) => {
    const isRootLeaf = branchChild == null;
    const isParentExpanded = expandedNodes.includes(parentExpandKey);

    return (
      <div key={cardKey} className="card p-3 fs-5 mb-2">
        <div className="d-flex align-items-center justify-content-between">
          <div
            className="d-flex align-items-center"
            style={{ cursor: isRootLeaf ? "default" : "pointer" }}
            onClick={() => {
              if (!isRootLeaf) toggleNode(parentExpandKey);
            }}
          >
            <i
              className={`bi ${
                isRootLeaf
                  ? "bi-dash"
                  : `bi-chevron-${isParentExpanded ? "down" : "right"}`
              } me-2`}
            />
            <span>{nodeLabel(root)}</span>
          </div>
          {/* Single toggle on top — reflects last hierarchy leaf; payload = leaf. */}
          {leaf && renderLeafToggle(leaf)}
        </div>

        {!isRootLeaf && isParentExpanded && branchChild && (
          <div className="ms-4 border-start ps-2 mt-2">
            {renderTreeNode(branchChild, leaf?.id ?? null)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card-toolbar flex-row-fluid justify-content-end gap-5 px-0 py-2">
      {dispositionLoading ? (
        <div className="text-center mt-3">
          <LoadingSpinner />
        </div>
      ) : alignedTree.length === 0 ? (
        <div className="text-muted py-4 fs-7">
          No dispositions found for this team.
        </div>
      ) : (
        <div style={{ overflow: "auto" }}>
          {branchCards.map((card) => renderBranchCard(card))}
        </div>
      )}
    </div>
  );
};

export default PriorityBasedInclusionList;